import React from "react";

import {
  IgCoreBackgroundColors,
  IgCoreTextColors,
} from "@intelligems/ig-design-tokens/constants";
import { ButtonBase, Stack, StackProps, useTheme } from "@mui/material";

import { IgButton } from "./IgButton";

export type IgSegmentedControlOption<T extends string> = {
  disabled?: boolean;
  label: React.ReactNode;
  value: T;
};

export type IgSegmentedControlProps<T extends string> = Omit<
  StackProps,
  "children" | "onChange"
> & {
  allowDeselect?: boolean;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: T | null) => void;
  options: IgSegmentedControlOption<T>[];
  value: T | null;
  variant?: "outlined" | "subtle";
};

/**
 * The design system's segmented control: a grey track with a 1px border and a
 * 6px radius, 26px segments inset by the track's padding, and the selected
 * segment as a white pill. 34px tall — Figma's 4px inset includes the
 * track's 1px stroke. Labels are Body 2: semibold primary when selected,
 * regular secondary otherwise.
 */
export const IgSegmentedControl = <T extends string>({
  allowDeselect = true,
  ariaLabel,
  disabled = false,
  onChange,
  options,
  sx,
  value,
  variant = "outlined",
  ...rest
}: IgSegmentedControlProps<T>) => {
  const theme = useTheme();

  const select = (option: IgSegmentedControlOption<T>) => {
    if (allowDeselect && value === option.value) {
      onChange(null);
      return;
    }
    onChange(option.value);
  };

  if (variant === "subtle") {
    return (
      <Stack
        direction="row"
        role="group"
        aria-label={ariaLabel}
        spacing={1}
        sx={[
          {
            backgroundColor: IgCoreBackgroundColors.interactive.neutral.default,
            borderRadius: "6px",
            p: 1,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <ButtonBase
              key={option.value}
              aria-pressed={isSelected}
              disabled={disabled || option.disabled}
              onClick={() => select(option)}
              sx={{
                flex: 1,
                height: 28,
                px: 3,
                borderRadius: "6px",
                fontSize: "14px",
                lineHeight: 1.5,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected
                  ? IgCoreTextColors.primary
                  : IgCoreTextColors.secondary,
                backgroundColor: isSelected
                  ? IgCoreBackgroundColors.baseLightHigh
                  : "transparent",
              }}
            >
              {option.label}
            </ButtonBase>
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      role="group"
      aria-label={ariaLabel}
      spacing={0.5}
      sx={[
        {
          backgroundColor: theme.igPrimitives.gray[400],
          border: `1px solid ${theme.igBorders.core.default}`,
          borderRadius: "6px",
          p: "3px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {options.map((option) => {
        const isSelected = value === option.value;

        return (
          <IgButton
            key={option.value}
            variant="text"
            size="mini"
            aria-pressed={isSelected}
            disabled={disabled || option.disabled}
            onClick={() => select(option)}
            sx={{
              flex: 1,
              justifyContent: "center",
              borderRadius: "4px",
              px: 3,
              fontWeight: isSelected ? 600 : 400,
              color: isSelected
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
              backgroundColor: isSelected
                ? theme.igPrimitives.gray[50]
                : "transparent",
              "&:hover": {
                backgroundColor: isSelected
                  ? theme.igPrimitives.gray[50]
                  : theme.igPrimitives.gray[600],
              },
            }}
          >
            {option.label}
          </IgButton>
        );
      })}
    </Stack>
  );
};
